import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import config from "../../../config";
import { apiDelete, apiError, apiGet, apiPost, apiPut, authHeaders, fmtPhone, fmtTime } from "../crm/leadsKit";
import { NO_DATA } from "../../lib/format";
import { fetchApi, reasonOf } from "../marketing/access";

/**
 * Shared data layer for the WhatsApp screens (design: state/whatsapp.tsx, state/waTemplates.ts).
 * Every value here comes from the backend under /api/waba/... — there is no sample data.
 * WhatsApp is configured per organisation (waba_configs), so none of these
 * endpoints take facility_id; the selected location does not filter them.
 */

export { apiDelete, apiError, apiGet, apiPost, apiPut, fmtPhone, fmtTime, NO_DATA };

/**
 * GET that keeps the reason of a failure (403 PERMISSION_DENIED, plan, outage) instead of
 * turning it into null like apiGet: → { ok, data, reason } where reason feeds <AccessNotice>.
 */
export async function getRes(path, params) {
  const r = await fetchApi(path, { params });
  return { ok: r.ok, status: r.status, data: r.data, reason: r.ok ? null : reasonOf(r) };
}

/** multipart POST (send-media, upload-media) — same headers as the old pages. */
export const apiUpload = (path, formData) =>
  axios.post(`${config.API_BASE_URL}${path}`, formData, { headers: { ...authHeaders().headers, "Content-Type": "multipart/form-data" } }).then((r) => r.data);

export const parseJson = (value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return fallback; }
};

export const toMs = (value) => (value ? new Date(value).getTime() : null);
export const num = (value) => Number(value) || 0;
export const pct = (value, total) => (total ? `${Math.round((value / total) * 100)}%` : "0%");
export const nf = (value) => (value === null || value === undefined ? NO_DATA : Number(value).toLocaleString("en-IN"));

/** Design language list (screens/whatsapp/Management.tsx). Codes match what the backend stores. */
export const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "hi", label: "Hindi" },
  { value: "mr", label: "Marathi" },
  { value: "gu", label: "Gujarati" },
  { value: "ta", label: "Tamil" },
  { value: "te", label: "Telugu" },
  { value: "kn", label: "Kannada" },
  { value: "bn", label: "Bengali" },
];
export const languageLabel = (code) => LANGUAGES.find((l) => l.value === code)?.label || (code === "en_US" || code === "en_GB" ? "English" : code || "English");

const titleCase = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1).toLowerCase() : "");

/* ------------------------------- Templates ------------------------------- */

export const CATEGORIES = ["Utility", "Marketing", "Authentication"];
export const categoryLabel = (key) => titleCase(key) || "Utility";
export const categoryKey = (label) => String(label || "Utility").toUpperCase();

/** waba_templates.approval_status: draft | pending | approved | rejected | paused (Meta paused it; item 250). */
export const TEMPLATE_STATUSES = ["Approved", "Pending", "Rejected", "Paused", "Draft"];
export const templateStatusLabel = (key) => titleCase(key) || "Draft";
export const templateStatusKey = (label) => String(label || "").toLowerCase();

const fileNameOf = (url) => (url ? decodeURIComponent(String(url).split("?")[0].split("/").pop() || "") : "");

/** Dialling codes the template builder offers for a Call button (longest first, so +971 wins over +97…). */
export const DIAL_CODES = ["+971", "+966", "+974", "+968", "+965", "+973", "+977", "+880", "+91", "+94", "+65", "+61", "+44", "+1"];

/** "+919876543210" (a button synced from Meta) → { countryCode: "+91", phone: "9876543210" }. */
const splitPhone = (raw) => {
  const value = String(raw.phone_number || "").replace(/[\s-]/g, "");
  if (raw.country_code) return { countryCode: raw.country_code, phone: value.replace(/^\+/, "") };
  if (!value.startsWith("+")) return { countryCode: "+91", phone: value };
  const code = DIAL_CODES.find((c) => value.startsWith(c)) || value.slice(0, 3);
  return { countryCode: code, phone: value.slice(code.length) };
};

/**
 * Backend button → design button. Our form saves { type, text, url | phone_number + country_code | offer_code };
 * a template synced from Meta carries Meta's own shape (phone_number "+91…", COPY_CODE example). The raw button is kept.
 */
const toButton = (b) => {
  const type = b.type === "URL" ? "url" : b.type === "PHONE_NUMBER" ? "phone" : b.type === "COPY_CODE" ? "copy_code" : "quick_reply";
  const code = b.offer_code || (Array.isArray(b.example) ? b.example[0] : typeof b.example === "string" ? b.example : "") || "";
  const phone = type === "phone" ? splitPhone(b) : { countryCode: "+91", phone: "" };
  return {
    type,
    text: type === "copy_code" ? b.text || "Copy offer code" : b.text || "",
    url: b.url || "",
    phone: phone.phone,
    countryCode: phone.countryCode,
    code,
    value: b.url || b.phone_number || code || undefined,
    raw: b,
  };
};

/** waba_templates row → design WhatsAppTemplate shape. */
export function toTemplate(row) {
  const headerType = String(row.header_type || "NONE").toUpperCase();
  const samples = parseJson(row.sample_values, {}) || {};
  const sampleValues = Array.isArray(samples)
    ? samples.map((v) => String(v ?? ""))
    : Object.keys(samples).sort((a, b) => Number(a) - Number(b)).map((k) => String(samples[k] ?? ""));
  const header = headerType === "TEXT"
    ? { type: "text", text: row.header_text || "" }
    : ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerType)
      ? { type: headerType.toLowerCase(), sampleUrl: row.header_media_url || undefined, sampleFileName: fileNameOf(row.header_media_url) || undefined }
      : undefined;
  return {
    id: String(row.id),
    name: row.template_name || "",
    category: categoryLabel(row.template_category),
    language: row.language || "en",
    status: templateStatusLabel(row.approval_status),
    rejectReason: row.rejection_reason || undefined,
    header,
    body: row.body_text || "",
    footer: row.footer_text || undefined,
    buttons: (parseJson(row.buttons, []) || []).map(toButton),
    sampleValues,
    // Item 249: sent / delivered / read / replied over the last 90 days, from GET /api/waba/templates.
    // A single GET /:id has no usage; then sent falls back to the all-time times_used.
    usage: row.usage
      ? { sent: num(row.usage.sent), delivered: num(row.usage.delivered), read: num(row.usage.read), replied: num(row.usage.replied), windowDays: num(row.usage.window_days) || 90 }
      : { sent: num(row.times_used), delivered: null, read: null, replied: null, windowDays: null },
    timesUsed: num(row.times_used),
    lastUsedAt: toMs(row.last_used_at),
    updatedAt: toMs(row.updated_at) || toMs(row.created_at),
    raw: row,
  };
}

/** Number of {{n}} variables a template body uses. */
export const variableCountOf = (body) => Math.max(0, ...[...String(body || "").matchAll(/\{\{(\d+)\}\}/g)].map((m) => Number(m[1])));

/** Like loadTemplates, but a failure keeps its reason: → { templates | null, reason }. */
export async function loadTemplatesResult(params = {}) {
  const r = await getRes("/api/waba/templates", { limit: 1000, ...params });
  return r.ok && r.data?.success !== false ? { templates: (r.data?.templates || []).map(toTemplate), reason: null } : { templates: null, reason: r.reason };
}

/** All active templates (the list endpoint pages at 20 by default). */
export async function loadTemplates(params = {}) {
  const r = await apiGet("/api/waba/templates", { limit: 1000, ...params });
  return r?.success ? (r.templates || []).map(toTemplate) : null;
}

/* ------------------------------- Campaigns ------------------------------- */

/** waba_campaigns.status: draft | scheduled | sending | paused | completed | failed | cancelled. */
export const CAMPAIGN_STATUSES = ["Draft", "Scheduled", "Sending", "Paused", "Completed", "Failed", "Cancelled"];
export const campaignStatusLabel = (key) => titleCase(key) || "Draft";
export const campaignStatusKey = (label) => String(label || "").toLowerCase();

export const RECIPIENT_TYPE_LABEL = { all: "All contacts and leads", patients: "Patients", leads: "Leads", contacts: "Contacts", custom_filter: "Custom filter" };

/** waba_campaigns row (+ per-recipient counts from the list endpoint) → design WaCampaign shape. */
export function toCampaign(row) {
  const sent = row.sent_count ?? row.messages_sent;
  const delivered = row.delivered_count ?? row.messages_delivered;
  const read = row.read_count ?? row.messages_read;
  const failed = row.failed_count ?? row.messages_failed;
  return {
    id: String(row.id),
    name: row.campaign_name || row.name || "",
    templateId: row.template_id ? String(row.template_id) : "",
    templateName: row.template_name || "",
    audience: { label: RECIPIENT_TYPE_LABEL[row.recipient_type] || titleCase(row.recipient_type) || NO_DATA, count: num(row.total_recipients) },
    status: campaignStatusLabel(row.status),
    statusKey: row.status || "draft",
    scheduledAt: toMs(row.scheduled_at),
    startedAt: toMs(row.started_at),
    completedAt: toMs(row.completed_at),
    // Item 235: replied_count is stored when the campaign's page counts its replies, so it is
    // null until someone has opened the campaign. Item 254: cost_usd is the billed cost, net of refunds (USD).
    stats: { sent: num(sent), delivered: num(delivered), read: num(read), replied: row.replied_count === null || row.replied_count === undefined ? null : num(row.replied_count), failed: num(failed) },
    repliedCountedAt: toMs(row.replied_counted_at),
    cost: row.cost_usd === null || row.cost_usd === undefined ? null : Number(row.cost_usd),
    archived: !!row.is_archived,
    createdAt: toMs(row.created_at),
    raw: row,
  };
}

/* ------------------------------ CSV audience ------------------------------ */

/** RFC 4180-ish CSV → rows of cells (quoted fields, "" escapes, CRLF or LF). */
export function parseCsv(text) {
  const rows = []; let row = []; let cell = ""; let quoted = false;
  const src = String(text || "").replace(/^\uFEFF/, "");
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') { if (src[i + 1] === '"') { cell += '"'; i++; } else quoted = false; } else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") { row.push(cell); cell = ""; }
    else if (ch === "\n" || ch === "\r") { if (ch === "\r" && src[i + 1] === "\n") i++; row.push(cell); rows.push(row); row = []; cell = ""; }
    else cell += ch;
  }
  if (cell !== "" || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((c) => String(c).trim() !== ""));
}

const PHONE_HEADERS = ["phone", "phone number", "mobile", "mobile number", "whatsapp", "number", "contact number"];
const NAME_HEADERS = ["name", "full name", "patient name", "contact name"];
const FIRST_HEADERS = ["first name", "firstname", "first_name"];
const LAST_HEADERS = ["last name", "lastname", "last_name", "surname"];
const CODE_HEADERS = ["country code", "country_code", "code", "isd"];

/**
 * CSV text → { rows: [{ phone, country_code, name }], skipped, error } for
 * POST /api/waba/campaigns/audience/csv (item 258). The header row names the
 * columns (phone / mobile, name or first + last name, country code); without a
 * recognisable header the first column is the phone and the second the name.
 * Numbers are checked by the backend; this only reads the file.
 */
export function csvAudienceRows(text) {
  const table = parseCsv(text);
  if (!table.length) return { rows: [], skipped: 0, error: "The file is empty." };
  const head = table[0].map((c) => String(c).trim().toLowerCase().replace(/\s+/g, " "));
  const find = (names) => head.findIndex((h) => names.includes(h));
  let phoneAt = find(PHONE_HEADERS);
  const hasHeader = phoneAt >= 0;
  if (!hasHeader) phoneAt = 0;
  const nameAt = hasHeader ? find(NAME_HEADERS) : 1;
  const firstAt = hasHeader ? find(FIRST_HEADERS) : -1;
  const lastAt = hasHeader ? find(LAST_HEADERS) : -1;
  const codeAt = hasHeader ? find(CODE_HEADERS) : -1;
  const rows = []; let skipped = 0;
  for (const cells of hasHeader ? table.slice(1) : table) {
    const phone = String(cells[phoneAt] ?? "").trim();
    if (!phone) { skipped++; continue; }
    const name = nameAt >= 0 ? String(cells[nameAt] ?? "").trim() : [firstAt >= 0 ? cells[firstAt] : "", lastAt >= 0 ? cells[lastAt] : ""].map((v) => String(v ?? "").trim()).filter(Boolean).join(" ");
    const code = codeAt >= 0 ? String(cells[codeAt] ?? "").trim() : "";
    rows.push({ phone, ...(code ? { country_code: code.startsWith("+") ? code : `+${code}` } : {}), ...(name ? { name } : {}) });
  }
  return { rows, skipped, error: rows.length ? "" : "No phone numbers found. Put them in a column named phone or mobile." };
}

/* ----------------------------- Conversations ----------------------------- */

/** waba_conversations.status is open | resolved | archived. "Waiting on patient" has no backend state. */
export const CONVERSATION_STATUSES = ["Open", "Waiting on patient", "Resolved"];
export const conversationStatusLabel = (key) => titleCase(key) || "Open";

export function toConversation(row) {
  return {
    id: String(row.id),
    phone: row.contact_phone || "",
    displayName: row.display_name || row.contact_name || "",
    contactType: row.contact_type || "unknown",
    contactId: row.contact_id ? String(row.contact_id) : "",
    status: conversationStatusLabel(row.status),
    statusKey: row.status || "open",
    assigneeId: row.assigned_to ? String(row.assigned_to) : "",
    assigneeName: row.assigned_to_name || "",
    lastMessage: row.last_message_text || "",
    lastMessageAt: toMs(row.last_message_at),
    lastDirection: row.last_message_direction === "outbound" ? "out" : row.last_message_direction === "inbound" ? "in" : "",
    lastSource: row.last_outbound_source || "",
    unread: num(row.unread_count),
    labels: parseJson(row.tags, []) || [],
    businessPhone: row.business_phone || "",
    raw: row,
  };
}

/** Relative media path (/uploads/...) → absolute; raw WhatsApp media ids are not viewable. Same rule as the old page. */
export const mediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${config.API_BASE_URL}${url}`;
  return null;
};

const sizeLabel = (bytes) => (!bytes ? undefined : bytes < 1_000_000 ? `${Math.max(1, Math.round(bytes / 1000))} KB` : `${(bytes / 1_000_000).toFixed(1)} MB`);

/** GET /api/waba/messages/conversation/:id returns newest first; callers reverse for display. */
export function toMessage(row) {
  const type = row.message_type || "text";
  const media = ["image", "video", "document"].includes(type)
    ? { type, url: mediaUrl(row.media_url) || undefined, fileName: row.media_filename || undefined, sizeLabel: sizeLabel(row.media_size) }
    : undefined;
  return {
    id: String(row.id),
    dir: row.direction === "outbound" ? "out" : "in",
    kind: type === "location" ? "location" : type === "image" ? "image" : type === "document" ? "document" : "text",
    body: row.message_text || "",
    status: row.status || "sent",
    failureReason: row.failure_reason || "",
    templateName: row.template_name || undefined,
    media,
    by: row.source === "ai_agent" || row.agent_handled ? "Olli AI" : row.sender_name || "",
    at: toMs(row.sent_at) || toMs(row.created_at) || 0,
    raw: row,
  };
}

/* --------------------------------- Config -------------------------------- */

/** GET /api/waba/config → the organisation's active WhatsApp number (or null). */
export function useWaConfig() {
  const [state, setState] = useState({ config: null, loading: true });
  const load = useCallback(() => {
    let alive = true;
    apiGet("/api/waba/config").then((r) => alive && setState({ config: r?.success ? r.config || null : null, loading: false }));
    return () => { alive = false; };
  }, []);
  useEffect(() => load(), [load]);
  return { ...state, reload: load };
}

/* ------------------------- Shared bits (Management.tsx) ------------------------- */

export const statusTone = (status) => (status === "Approved" || status === "Completed" ? "bg-success-soft text-success" : status === "Rejected" || status === "Failed" ? "bg-danger-soft text-danger" : "bg-mist text-ink");

export function StatusWord({ status, title }) {
  return <span title={title} className={`inline-flex rounded-full px-2.5 py-1 text-caption font-semibold ${statusTone(status)}`}>{status}</span>;
}

export function PageHeading({ title, subtitle, action }) {
  return <header className="flex flex-wrap items-end justify-between gap-4"><div><h1 className="text-h1 font-semibold">{title}</h1><p className="mt-1 text-small text-muted">{subtitle}</p></div>{action}</header>;
}
